import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { BlogPostAdminDto } from '@ecom/contracts';
import { FilterBar } from '@/components/filters';
import { FileIcon, PlusIcon } from '@/components/icons';
import { ListCard, ListToolbar, LoadMore, Page, Segmented, Table, TableScroll, Td, Th, Tr } from '@/components/page';
import { Alert, Button, EmptyState, Pill, TableSkeleton, type PillTone } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { date } from '@/lib/format';
import { useBlogStore } from '@/stores/blog.store';

/** Where a post stands: a draft, out, or waiting for its date (ADR-080). */
export function postState(post: Pick<BlogPostAdminDto, 'status' | 'isLive'>): 'draft' | 'live' | 'scheduled' {
  if (post.status === 'DRAFT') return 'draft';
  return post.isLive ? 'live' : 'scheduled';
}

const TONE: Record<ReturnType<typeof postState>, PillTone> = { draft: 'neutral', live: 'success', scheduled: 'info' };

/**
 * The store's blog (ADR-080): every post, searched and paged on the server —
 * drafts, the ones waiting for their date, and the ones out.
 */
export function BlogPage() {
  const t = useT();
  const navigate = useNavigate();
  const locale = useUiStore((state) => state.locale);
  const { items, nextCursor, total, search, status, loading, error, load, loadMore } = useBlogStore();

  useEffect(() => {
    void load();
  }, [load, locale]);

  const write = (
    <Button onClick={() => navigate('/blog/new')}>
      <PlusIcon className="size-4" />
      {t('blogWrite')}
    </Button>
  );

  return (
    <Page title={t('blog')} subtitle={t('blogSubtitle')} actions={write} fill>
      <ListCard>
        <ListToolbar>
          <div className="flex flex-wrap items-center gap-3">
            <Segmented
              label={t('blogStatus')}
              items={[
                { id: '', label: t('blogAll') },
                { id: 'PUBLISHED', label: t('blogPublished') },
                { id: 'DRAFT', label: t('blogDrafts') },
              ]}
              value={status}
              onChange={(next) => void load({ status: next as typeof status })}
            />
            <div className="min-w-48 flex-1">
              <FilterBar search={search} onSearch={(term) => void load({ search: term })} placeholder={t('blogSearch')} total={total ?? undefined} shown={items.length} />
            </div>
          </div>
        </ListToolbar>

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading && items.length === 0 ? (
          <TableSkeleton columns={4} />
        ) : items.length === 0 ? (
          <EmptyState title={t('blogEmpty')} body={t('blogEmptyHint')} icon={<FileIcon />} action={write} />
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('blogPostTitle')}</Th>
                  <Th>{t('blogStatus')}</Th>
                  <Th>{t('blogDate')}</Th>
                  <Th>{t('blogAuthor')}</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((post) => {
                  const state = postState(post);
                  return (
                    <Tr key={post.id} onClick={() => navigate(`/blog/${post.id}`)}>
                      <Td>
                        <span className="block max-w-96 truncate font-medium text-ink">{post.title}</span>
                        <code dir="ltr" className="text-xs text-ink-faint">
                          /blog/{post.slug}
                        </code>
                      </Td>
                      <Td>
                        <Pill tone={TONE[state]}>{t(`blogState_${state}`)}</Pill>
                      </Td>
                      <Td className="whitespace-nowrap text-ink-muted">{post.publishedAt ? date(post.publishedAt, locale) : '—'}</Td>
                      <Td className="text-ink-muted">{post.author?.name ?? '—'}</Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </TableScroll>
        )}

        <LoadMore visible={Boolean(nextCursor)} loading={loading} onClick={() => void loadMore()} />
      </ListCard>
    </Page>
  );
}
